import { useEffect, useMemo, useRef, useState } from "react";
import { IFood } from "../../models/IFood";
import { getAllFood } from "../../services/food";
import {
  ICreatePhoneOrder, IPhoneCustomer, IPhoneOrder, IPhoneOrderNewLine,
  PHONE_MAX_LINES, PHONE_MAX_NAME, PHONE_MAX_NOTE, PHONE_MAX_PHONE_INPUT,
  PHONE_MAX_QUANTITY, PHONE_PAYMENT_METHODS,
} from "../models/IPhoneOrder";
import { createPhoneOrder, phoneErrorMessage } from "../services/phoneOrderService";
import PhoneAddressFields, { IPhoneAddressValue } from "./PhoneAddressFields";
import PhoneCustomerSearchBox from "./PhoneCustomerSearchBox";

const CREATE_ERROR: Record<string, string> = {
  DeliveryInformationRequired: "Teslimat bilgisi eksik. Kayıtlı müşteri seçseniz bile tüm alanlar gönderilmeli.",
  InvalidPhoneNumber: "Telefon numarası geçerli değil. 0533 123 45 67 gibi yazın.",
  CityNotFound: "Seçilen ilçe bulunamadı, listeyi yenileyin.",
  TownNotFound: "Seçilen bölge bulunamadı, listeyi yenileyin.",
  TownNotInCity: "Seçilen bölge bu ilçeye ait değil. İlçeyi değiştirip bölgeyi yeniden seçin.",
  PhoneCustomerNotFound: "Seçilen müşteri kaydı artık yok. Aramayı yenileyin.",
  PhoneCustomerNotInRestaurant: "Seçilen müşteri kaydı başka bir restorana ait. Aramayı bu restoranda yapın.",
  EmptyOrderLineRequest: "Siparişe en az bir ürün eklenmeli.",
  TooManyOrderLines: `Bir siparişte en fazla ${PHONE_MAX_LINES} satır olabilir. Siparişi bölün.`,
  InvalidLineQuantity: `Adet 1 ile ${PHONE_MAX_QUANTITY} arasında olmalı.`,
  FoodNotFound: "Seçilen ürünlerden biri bulunamadı. Menüyü yenileyin.",
  FoodNotInRestaurant: "Seçilen ürünlerden biri bu restorana ait değil. Menüyü yenileyin.",
  FoodNotAvailable: "Seçilen ürünlerden biri satışta değil. Menüyü yenileyin.",
  MenuUpgradeNotFound: "Seçilen menü eklerinden biri bulunamadı.",
  MenuUpgradeNotForFood: "Menü eki seçilen ürüne ait değil.",
  MenuUpgradeNotAvailable: "Menü eki satışta değil.",
  MenuUpgradeQuantityExceeded: "Menü eki için izin verilen adet aşıldı.",
  MixedCurrency: "Bir siparişte tek para birimi olabilir. Farklı para birimli ürünleri ayrı sipariş yapın.",
  PaymentMethodNotAllowed: "Telefon siparişinde yalnızca Nakit ve Kapıda Kredi Kartı seçilebilir.",
  OrderStatusUnavailable: "Sistem hatası: sipariş durumu tanımı eksik. Siparişi tekrar göndermeyin, destek ekibine bildirin.",
  UnspecifiedPaymentMethodUnavailable: "Sistem hatası: ödeme yöntemi tanımı eksik. Bir ödeme yöntemi seçip yeniden deneyin.",
  RestourantAccessDenied: "Bu restoranda sipariş oluşturma yetkiniz yok.",
};

const DIRECTORY_OUTCOME_TEXT: Record<string, string> = {
  NotSaved: "Müşteri dizine kaydedilmedi.",
  Created: "Müşteri dizine yeni kayıt olarak eklendi.",
  Updated: "Dizindeki mevcut müşteri kaydı güncellendi.",
};

interface DraftLine {
  key: string;
  food: IFood;
  quantity: number;
  note: string;
  upgrades: Record<string, number>;
}

function money(n: number, symbol?: string): string {
  return `${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${symbol ? ` ${symbol}` : ""}`;
}

const priceOf = (f: IFood) => Number(f.foodPrice) || 0;
const effectivePriceOf = (f: IFood) => (f.hasDiscount && f.discountedPrice != null ? f.discountedPrice : priceOf(f));

interface Props {
  restaurantId: string;
  /** Sipariş oluşunca kayıtlar sekmesine geçmek için. */
  onGoToLedger: () => void;
}

export default function NewPhoneOrderTab({ restaurantId, onGoToLedger }: Props) {
  /* müşteri */
  const [customer, setCustomer] = useState<IPhoneCustomer | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [saveToDirectory, setSaveToDirectory] = useState(false);

  /* teslimat */
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState<IPhoneAddressValue>({
    cityId: "", townId: "", streetName: "", addressDetail: "",
  });

  /* ürünler */
  const [menu, setMenu] = useState<IFood[] | null>(null);
  const [menuError, setMenuError] = useState("");
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [recentKey, setRecentKey] = useState<string | null>(null);
  // Aynı ürün tekrar eklenince de vurgu animasyonu yeniden oynasın diye.
  const [flashTick, setFlashTick] = useState(0);
  const recentRef = useRef<HTMLDivElement>(null);

  /* ödeme ve not */
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [orderNote, setOrderNote] = useState("");

  /* gönderim */
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<IPhoneOrder | null>(null);

  useEffect(() => {
    setMenu(null);
    setMenuError("");
    getAllFood(restaurantId)
      .then((res) => setMenu(res.data ?? []))
      .catch(() => setMenuError("Menü yüklenemedi."));
  }, [restaurantId]);

  const sellable = useMemo(() => (menu ?? []).filter((f) => f.foodIsEnabled), [menu]);

  const visible = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return sellable;
    return sellable.filter((f) =>
      f.foodNameTr?.toLocaleLowerCase("tr").includes(q) || f.foodNameEn?.toLocaleLowerCase("tr").includes(q)
    );
  }, [sellable, search]);

  // Siparişte tek para birimi olabilir; ilk eklenen ürün birimi kilitler.
  const lockedCurrencyId = draft[0]?.food.currencyId;

  const pickCustomer = (c: IPhoneCustomer) => {
    setCustomer(c);
    setManualMode(false);
    setFirstName(c.firstName);
    setLastName(c.lastName);
    setPhoneNumber(c.phoneNumber);
    setAddress({
      cityId: c.cityId, townId: c.townId,
      streetName: c.streetName, addressDetail: c.addressDetail,
    });
    setSaveToDirectory(false);
    setError("");
  };

  const startManual = (query: string) => {
    setCustomer(null);
    setManualMode(true);
    if (/^[\d+]/.test(query)) setPhoneNumber(query); else setFirstName(query);
    setError("");
  };

  const clearCustomer = () => {
    setCustomer(null);
    setManualMode(false);
    setFirstName(""); setLastName(""); setPhoneNumber("");
    setAddress({ cityId: "", townId: "", streetName: "", addressDetail: "" });
    setSaveToDirectory(false);
  };

  const patchAddress = (patch: Partial<IPhoneAddressValue>) => setAddress((p) => ({ ...p, ...patch }));

  /* sepet */
  // Son eklenen satır listenin en başına gelir ve açık sarıyla vurgulanır (salondaki gibi).
  const addToDraft = (food: IFood) => {
    setError("");
    const existing = draft.find((d) => d.food.id === food.id && !d.note && Object.keys(d.upgrades).length === 0);
    if (existing) {
      const bumped = { ...existing, quantity: Math.min(PHONE_MAX_QUANTITY, existing.quantity + 1) };
      setDraft((prev) => [bumped, ...prev.filter((d) => d.key !== existing.key)]);
      setRecentKey(existing.key);
    } else {
      const key = `${food.id}-${Date.now()}`;
      setDraft((prev) => [{ key, food, quantity: 1, note: "", upgrades: {} }, ...prev]);
      setRecentKey(key);
    }
    setFlashTick((t) => t + 1);
  };

  // Sayfa her eklemede zıplamasın: eklenen satır yalnız görünmüyorsa (ya da alttaki sabit
  // çubuğun altında kalıyorsa) ona kaydırılır.
  useEffect(() => {
    if (flashTick === 0) return;
    const el = recentRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const barHeight = 110;
    const hidden = rect.top < 0 || rect.bottom > window.innerHeight - barHeight;
    if (hidden) {
      window.scrollBy({ top: rect.top - window.innerHeight / 3, behavior: "smooth" });
    }
  }, [flashTick]);

  const patchDraft = (key: string, patch: Partial<DraftLine>) =>
    setDraft((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));

  const removeDraft = (key: string) => setDraft((prev) => prev.filter((d) => d.key !== key));

  const setUpgradeQty = (key: string, upgradeId: string, qty: number) =>
    setDraft((prev) => prev.map((d) => {
      if (d.key !== key) return d;
      const next = { ...d.upgrades };
      if (qty <= 0) delete next[upgradeId]; else next[upgradeId] = qty;
      return { ...d, upgrades: next };
    }));

  const draftTotal = draft.reduce((sum, d) => {
    const upg = Object.entries(d.upgrades).reduce((s, [id, q]) => {
      const u = d.food.menuUpgrade?.find((x) => x.id === id);
      return s + (u ? u.menuUpgradePrice * q : 0);
    }, 0);
    return sum + effectivePriceOf(d.food) * d.quantity + upg;
  }, 0);

  const deliveryComplete =
    !!firstName.trim() && !!lastName.trim() && !!phoneNumber.trim() &&
    !!address.cityId && !!address.townId && !!address.streetName.trim() && !!address.addressDetail.trim();

  const overLimit = draft.length > PHONE_MAX_LINES;
  const canSubmit = deliveryComplete && draft.length > 0 && !overLimit && !saving;

  const resetAll = () => {
    clearCustomer();
    setDraft([]);
    setSearch("");
    setPaymentMethodId("");
    setOrderNote("");
    setError("");
    setResult(null);
  };

  const handleSubmit = () => {
    if (!canSubmit) {
      if (!deliveryComplete) setError("Teslimat bilgisinin tüm alanları zorunludur.");
      else if (draft.length === 0) setError("Siparişe en az bir ürün ekleyin.");
      else if (overLimit) setError(`Bir siparişte en fazla ${PHONE_MAX_LINES} satır olabilir.`);
      return;
    }

    const lines: IPhoneOrderNewLine[] = draft.map((d) => {
      const upgrades = Object.entries(d.upgrades).map(([menuUpgradeId, quantity]) => ({ menuUpgradeId, quantity }));
      return {
        foodId: d.food.id,
        quantity: d.quantity,
        foodOrderNote: d.note.trim() || undefined,
        upgrades: upgrades.length ? upgrades : undefined,
      };
    });

    const body: ICreatePhoneOrder = {
      restourantId: restaurantId,
      delivery: {
        phoneCustomerId: customer?.id,
        saveToDirectory,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber.trim(),
        cityId: address.cityId,
        townId: address.townId,
        streetName: address.streetName.trim(),
        addressDetail: address.addressDetail.trim(),
      },
      lines,
      paymentMethodId: paymentMethodId || undefined,
      orderNote: orderNote.trim() || undefined,
    };

    setSaving(true);
    setError("");
    createPhoneOrder(body)
      .then((res) => setResult(res.data))
      .catch((err) => setError(phoneErrorMessage(err, CREATE_ERROR, "Sipariş oluşturulamadı.")))
      .finally(() => setSaving(false));
  };

  /* ------------------------------------------------------------- sonuç fişi */

  if (result) {
    return (
      <div className="po-receipt">
        <div className="po-receipt__icon"><i className="bi bi-check-circle-fill"></i></div>
        <h5 className="po-receipt__title">Sipariş alındı</h5>
        <div className="po-receipt__code">{result.orderCode}</div>
        <div className="po-receipt__status">
          <span className="dinein-badge dinein-badge--open">{result.orderStatusNameTr}</span>
        </div>

        <dl className="po-receipt__grid">
          <div><dt>Müşteri</dt><dd>{result.delivery.firstName} {result.delivery.lastName}</dd></div>
          <div><dt>Telefon</dt><dd>{result.delivery.phoneNumber}</dd></div>
          <div className="po-receipt__wide">
            <dt>Adres</dt>
            <dd>{result.delivery.cityName}, {result.delivery.townName}, {result.delivery.streetName}, {result.delivery.addressDetail}</dd>
          </div>
          <div><dt>Ürün</dt><dd>{result.lines.length} satır</dd></div>
          <div>
            <dt>Ödeme</dt>
            <dd>{result.isPaymentMethodSpecified ? result.paymentMethodNameTr : "Belirlenmedi"}</dd>
          </div>
        </dl>

        <div className="po-receipt__total">
          <span>Tahsil edilecek</span>
          <strong>{money(result.totalAmount, result.currencyDisplayText)}</strong>
        </div>
        {result.totalDiscountAmount > 0 && (
          <div className="dinein-small dinein-muted" style={{ textAlign: "center" }}>
            Kampanyasız {money(result.subTotalAmount, result.currencyDisplayText)} ·
            indirim {money(result.totalDiscountAmount, result.currencyDisplayText)}
          </div>
        )}

        <div className="dinein-alert dinein-alert--info" style={{ marginTop: 14 }}>
          <i className="bi bi-journal-bookmark"></i>
          <span>{DIRECTORY_OUTCOME_TEXT[result.directoryOutcome] ?? result.directoryOutcome}</span>
        </div>

        {!result.isPaymentMethodSpecified && (
          <div className="dinein-alert dinein-alert--warn">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>Ödeme yöntemi belirtilmedi. Teslimattan sonra "Sipariş Kayıtları" sekmesinden girebilirsiniz.</span>
          </div>
        )}

        <div className="po-receipt__actions">
          <button className="dinein-btn dinein-btn--primary" onClick={resetAll}>
            <i className="bi bi-telephone-plus-fill me-1"></i>Yeni Sipariş
          </button>
          <button className="dinein-btn dinein-btn--ghost" onClick={onGoToLedger}>
            <i className="bi bi-list-ul me-1"></i>Sipariş Kayıtları
          </button>
        </div>
      </div>
    );
  }

  /* ----------------------------------------------------------------- form */

  return (
    <div className="po-new">
      <div className="po-new__cols">
        {/* --- Sol: müşteri, teslimat, ödeme --- */}
        <div className="po-new__left">
          <section className="po-section">
            <div className="po-section__head">
              <span className="po-step">1</span>
              <div>
                <h6>Müşteri</h6>
                <p>Dizinde arayın; yoksa bilgileri elle girin.</p>
              </div>
            </div>

            {customer ? (
              <div className="po-picked">
                <div>
                  <div className="po-picked__name">{customer.firstName} {customer.lastName}</div>
                  <div className="po-picked__meta">
                    <i className="bi bi-telephone me-1"></i>{customer.phoneNumber}
                  </div>
                  <div className="po-picked__meta">
                    <i className="bi bi-geo-alt me-1"></i>{customer.cityName}, {customer.townName}
                  </div>
                </div>
                <button className="dinein-icon-btn" title="Seçimi kaldır" onClick={clearCustomer}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            ) : manualMode ? (
              <div className="po-manual">
                <i className="bi bi-pencil-fill me-2"></i>
                <span>Kayıtsız müşteri — bilgileri aşağıda girin.</span>
                <button className="dinein-link" onClick={() => setManualMode(false)}>Dizinde ara</button>
              </div>
            ) : (
              <>
                <PhoneCustomerSearchBox
                  restaurantId={restaurantId}
                  selectedId={null}
                  onSelect={pickCustomer}
                  onCreateNew={startManual}
                />
                <button className="dinein-link" onClick={() => startManual("")}>
                  <i className="bi bi-skip-forward me-1"></i>Aramadan devam et, bilgileri elle gireyim
                </button>
              </>
            )}
          </section>

          <section className="po-section">
            <div className="po-section__head">
              <span className="po-step">2</span>
              <div>
                <h6>Teslimat Bilgisi</h6>
                <p>Kayıtlı müşteri seçseniz de tüm alanlar siparişe yazılır.</p>
              </div>
            </div>

            <div className="pc-form__row">
              <div className="pc-field">
                <label>Ad <span className="pc-req">*</span></label>
                <input className="form-control" value={firstName} maxLength={PHONE_MAX_NAME}
                  onChange={(e) => setFirstName(e.target.value)} placeholder="Ayşe" />
              </div>
              <div className="pc-field">
                <label>Soyad <span className="pc-req">*</span></label>
                <input className="form-control" value={lastName} maxLength={PHONE_MAX_NAME}
                  onChange={(e) => setLastName(e.target.value)} placeholder="Yılmaz" />
              </div>
            </div>

            <div className="pc-field">
              <label>Telefon <span className="pc-req">*</span></label>
              <input className="form-control" value={phoneNumber} maxLength={PHONE_MAX_PHONE_INPUT}
                onChange={(e) => setPhoneNumber(e.target.value)} placeholder="0533 123 45 67" />
            </div>

            <PhoneAddressFields value={address} onChange={patchAddress} />

            <label className="po-check">
              <input type="checkbox" checked={saveToDirectory} onChange={(e) => setSaveToDirectory(e.target.checked)} />
              <span>
                {customer
                  ? "Bu adresi müşterinin dizin kaydına da işle"
                  : "Bu müşteriyi dizine kaydet"}
              </span>
            </label>
            <div className="dinein-hint">
              <i className="bi bi-info-circle me-1"></i>
              {customer
                ? "İşaretlenirse dizindeki kayıt bu bilgilerle güncellenir. Geçmiş siparişler etkilenmez."
                : "Numara dizinde zaten varsa yeni kayıt açılmaz, mevcut kayıt güncellenir."}
            </div>
          </section>

          <section className="po-section">
            <div className="po-section__head">
              <span className="po-step">3</span>
              <div>
                <h6>Ödeme ve Not</h6>
                <p>Ödeme yöntemi şimdi girilmek zorunda değil.</p>
              </div>
            </div>

            <div className="pay-methods">
              <div className="pay-methods__label">Ödeme Yöntemi</div>
              <div className="pay-methods__options pay-methods__options--3">
                {PHONE_PAYMENT_METHODS.map((m) => (
                  <button key={m.id} type="button"
                    className={`pay-method${paymentMethodId === m.id ? " pay-method--active" : ""}`}
                    onClick={() => setPaymentMethodId(m.id)}>
                    {paymentMethodId === m.id && <i className="bi bi-check-circle-fill pay-method__check"></i>}
                    <i className={`bi ${m.icon}`}></i>
                    <span>{m.label}</span>
                  </button>
                ))}
                {/* "Sonra" bir onay değil erteleme: nötr renkte durur. */}
                <button type="button"
                  className={`pay-method pay-method--defer${paymentMethodId === "" ? " pay-method--active" : ""}`}
                  onClick={() => setPaymentMethodId("")}>
                  <i className="bi bi-clock-history"></i>
                  <span>Sonra girilsin</span>
                </button>
              </div>
              {paymentMethodId === "" && (
                <div className="dinein-hint" style={{ marginTop: 8 }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Sipariş "Belirlenmedi" ile oluşur; teslimattan sonra kayıtlar sekmesinden girilir.
                </div>
              )}
            </div>

            <div className="pc-field" style={{ marginTop: 12 }}>
              <label>Sipariş Notu</label>
              <textarea className="form-control" rows={2} value={orderNote} maxLength={PHONE_MAX_NOTE}
                placeholder="Zili çalmayın, bebek uyuyor"
                onChange={(e) => setOrderNote(e.target.value)} />
            </div>
          </section>
        </div>

        {/* --- Sağ: menü ve sepet --- */}
        <div className="po-new__right">
          <section className="po-section po-section--menu">
            <div className="po-section__head">
              <span className="po-step">4</span>
              <div>
                <h6>Ürünler</h6>
                <p>Fiyat ve kampanya sunucuda hesaplanır.</p>
              </div>
            </div>

            <div className="dinein-search">
              <i className="bi bi-search"></i>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ürün ara..." />
              {search && <button onClick={() => setSearch("")}><i className="bi bi-x-lg"></i></button>}
            </div>

            {menuError ? (
              <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{menuError}</span></div>
            ) : !menu ? (
              <div className="dinein-center"><span className="spinner-border spinner-border-sm" /></div>
            ) : visible.length === 0 ? (
              <div className="dinein-center">{search ? `"${search}" ile eşleşen ürün yok.` : "Satışta ürün yok."}</div>
            ) : (
              <>
              <div className="po-menu-count">
                <span>{visible.length} ürün</span>
                {visible.length > 7 && <span><i className="bi bi-arrow-down-up me-1"></i>listede kaydırın</span>}
              </div>
              <div className="dinein-food-list po-food-list">
                {visible.map((f) => {
                  const blocked = !!lockedCurrencyId && f.currencyId !== lockedCurrencyId;
                  const disc = f.hasDiscount && f.discountedPrice != null;
                  return (
                    <button key={f.id} type="button" className="dinein-food" disabled={blocked}
                      title={blocked ? "Bu ürün siparişin para biriminden farklı" : undefined}
                      onClick={() => addToDraft(f)}>
                      <span className="dinein-food__name">{f.foodNameTr}</span>
                      <span className="dinein-food__price">
                        {disc && <s>{money(priceOf(f))}</s>}
                        <strong>{money(effectivePriceOf(f))}</strong>
                      </span>
                      {blocked && <span className="dinein-badge dinein-badge--warn">farklı para birimi</span>}
                    </button>
                  );
                })}
              </div>
              </>
            )}
          </section>

          <section className="po-section">
            <div className="po-section__cart-head">
              <span>Sipariş İçeriği</span>
              <span className={overLimit ? "dinein-count dinein-count--over" : "dinein-count"}>
                {draft.length} / {PHONE_MAX_LINES} satır
              </span>
            </div>

            {draft.length === 0 ? (
              <div className="dinein-center">
                <i className="bi bi-basket"></i>
                <div>Menüden ürün seçin.</div>
              </div>
            ) : (
              <div className="dinein-draft-list">
                {draft.map((d) => {
                  const upgrades = d.food.menuUpgrade?.filter((u) => u.menuUpgradeIsEnabled) ?? [];
                  const expanded = expandedKey === d.key;
                  const recent = d.key === recentKey;
                  return (
                    <div key={recent ? `${d.key}-${flashTick}` : d.key}
                      ref={recent ? recentRef : undefined}
                      className={`dinein-draft${recent ? " dinein-draft--recent" : ""}`}>
                      <div className="dinein-draft__top">
                        <span className="dinein-draft__name">{d.food.foodNameTr}</span>
                        <button className="dinein-icon-btn dinein-icon-btn--danger" onClick={() => removeDraft(d.key)}>
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>

                      <div className="dinein-draft__row">
                        <div className="dinein-stepper">
                          <button type="button" disabled={d.quantity <= 1}
                            onClick={() => patchDraft(d.key, { quantity: Math.max(1, d.quantity - 1) })}>
                            <i className="bi bi-dash-lg"></i>
                          </button>
                          <input type="number" min={1} max={PHONE_MAX_QUANTITY} value={d.quantity}
                            onChange={(e) => patchDraft(d.key, { quantity: Math.min(PHONE_MAX_QUANTITY, Math.max(1, Number(e.target.value) || 1)) })} />
                          <button type="button" disabled={d.quantity >= PHONE_MAX_QUANTITY}
                            onClick={() => patchDraft(d.key, { quantity: Math.min(PHONE_MAX_QUANTITY, d.quantity + 1) })}>
                            <i className="bi bi-plus-lg"></i>
                          </button>
                        </div>
                        <span className="dinein-draft__price">
                          {money(effectivePriceOf(d.food) * d.quantity)}
                        </span>
                      </div>

                      <input className="dinein-note-input" value={d.note} maxLength={PHONE_MAX_NOTE}
                        placeholder="Not (örn. soğansız)"
                        onChange={(e) => patchDraft(d.key, { note: e.target.value })} />

                      {upgrades.length > 0 && (
                        <>
                          <button type="button" className="dinein-link" onClick={() => setExpandedKey(expanded ? null : d.key)}>
                            <i className={`bi bi-chevron-${expanded ? "up" : "down"} me-1`}></i>
                            Menü ekleri {Object.keys(d.upgrades).length > 0 && `(${Object.keys(d.upgrades).length})`}
                          </button>
                          {expanded && (
                            <div className="dinein-upgrades">
                              {upgrades.map((u) => {
                                const qty = d.upgrades[u.id] ?? 0;
                                return (
                                  <div key={u.id} className="dinein-upgrade">
                                    <span>{u.menuUpgradeTitle} <em>{money(u.menuUpgradePrice)}</em></span>
                                    <div className="dinein-stepper dinein-stepper--sm">
                                      <button type="button" disabled={qty <= 0} onClick={() => setUpgradeQty(d.key, u.id, qty - 1)}>
                                        <i className="bi bi-dash-lg"></i>
                                      </button>
                                      <span>{qty}</span>
                                      <button type="button" disabled={qty >= u.menuUpgradeMaxQuantity}
                                        onClick={() => setUpgradeQty(d.key, u.id, qty + 1)}>
                                        <i className="bi bi-plus-lg"></i>
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                              <div className="dinein-hint">Ek adedi yemek adediyle çarpılmaz — mutlak adettir.</div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      {error && (
        <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
      )}

      {/* Alt sabit çubuk: toplam ve gönderim. Çift gönderim iki sipariş demek, buton kilitli. */}
      <div className="po-bar">
        <div className="po-bar__info">
          <div className="po-bar__total">
            <span>Tahmini tutar</span>
            <strong>{money(draftTotal)}</strong>
          </div>
          <div className="po-bar__checks">
            <span className={deliveryComplete ? "po-ok" : "po-pending"}>
              <i className={`bi ${deliveryComplete ? "bi-check-circle-fill" : "bi-circle"} me-1`}></i>Teslimat
            </span>
            <span className={draft.length > 0 ? "po-ok" : "po-pending"}>
              <i className={`bi ${draft.length > 0 ? "bi-check-circle-fill" : "bi-circle"} me-1`}></i>Ürün
            </span>
          </div>
        </div>
        <button className="dinein-btn dinein-btn--primary dinein-btn--lg" onClick={handleSubmit} disabled={!canSubmit}>
          {saving
            ? <><span className="spinner-border spinner-border-sm me-1" />Gönderiliyor...</>
            : <><i className="bi bi-telephone-outbound-fill me-1"></i>Siparişi Oluştur</>}
        </button>
      </div>
    </div>
  );
}
